import { YYYY_MM_DD } from "@/features/event-details/_logic/date-utils";
import { formatAgendaDateLong } from "@/features/event-details-agenda/_logic/date-format";
import type { FoodBeverageData, FoodBeverageMeals } from "../_types/wizard-types";
import { FOOD_BEVERAGE_MEAL_OPTIONS } from "./constants";

/**
 * Narrows an untrusted meal selection (client state or draft jsonb) to the allowlisted services
 * and well-formed, sorted, de-duplicated days.
 *
 * @param raw - Raw selection value.
 * @returns The sanitized selection; `{}` when nothing valid is present.
 */
export function sanitizeFoodBeverageMeals(raw: unknown): FoodBeverageMeals {
  if (!raw || typeof raw !== "object") return {};
  const value = raw as Record<string, unknown>;
  const meals: FoodBeverageMeals = {};
  for (const meal of FOOD_BEVERAGE_MEAL_OPTIONS) {
    const days = value[meal];
    if (!Array.isArray(days)) continue;
    meals[meal] = [...new Set(days.filter((d): d is string => typeof d === "string" && YYYY_MM_DD.test(d)))].sort();
  }
  return meals;
}

/**
 * Folds the selected services (with their days) and the free-text detail into the single
 * `events.food_beverage_requirements` string venues read, e.g.
 * `"Coffee & tea; Lunch: Wed Sep 23, Thu Sep 24. Vegetarian options please"`.
 *
 * @param foodBeverage - The Food & beverage step's answer.
 * @returns The combined text, or `""` when neither selections nor detail were given.
 */
export function formatFoodBeverageRequirements(foodBeverage: FoodBeverageData): string {
  const meals = sanitizeFoodBeverageMeals(foodBeverage.meals);
  const selections = FOOD_BEVERAGE_MEAL_OPTIONS.filter((meal) => meals[meal])
    .map((meal) => {
      const days = meals[meal] ?? [];
      if (days.length === 0) return meal;
      return `${meal}: ${days.map((d) => formatAgendaDateLong(d).replace(",", "")).join(", ")}`;
    })
    .join("; ");
  const details = foodBeverage.details.trim();
  return [selections, details].filter(Boolean).join(". ");
}

/**
 * Answers "Food & beverage needed?". "No" clears the selections and detail so they don't silently
 * resurface (and get saved) if the planner answers "Yes" again.
 *
 * @param foodBeverage - The current answer.
 * @param needed - The new yes/no.
 * @returns The next answer.
 */
export function setFoodBeverageNeeded(foodBeverage: FoodBeverageData, needed: boolean): FoodBeverageData {
  if (!needed) return { needed: false, meals: {}, details: "" };
  return { ...foodBeverage, needed: true };
}

/** Saved text that answers "not needed", e.g. "No", "None", "N/A", "No catering needed". */
const NOT_NEEDED =
  /^(?:no|none|n\/a)(?:\s+(?:catering|food(?:\s*(?:&|and)\s*beverage)?|f&b))?(?:\s+(?:needed|required))?[.!]?$/i;

/**
 * Inverse of {@link formatFoodBeverageRequirements}, for editing a saved event. Reads each leading
 * `"Meal; Meal: Wed Sep 23"` piece that names a known service with day labels from `dayOptions`
 * back into a pill; pieces that don't match stay as free text ahead of the detail, so hand-written
 * values keep their wording.
 *
 * @param text - Saved `events.food_beverage_requirements`.
 * @param dayOptions - Event days (`YYYY-MM-DD`) the selections may reference.
 * @returns The answer; `needed` is false when the column is empty or says no.
 */
export function parseFoodBeverageRequirements(text: string | null, dayOptions: string[]): FoodBeverageData {
  const value = text?.trim() ?? "";
  if (!value || NOT_NEEDED.test(value)) return { needed: false, meals: {}, details: "" };
  if (value === "Yes") return { needed: true, meals: {}, details: "" };

  const dateByLabel = new Map(dayOptions.map((d) => [formatAgendaDateLong(d).replace(",", ""), d]));
  const splitAt = value.indexOf(". ");
  const head = splitAt === -1 ? value : value.slice(0, splitAt);
  const meals: FoodBeverageMeals = {};
  const unmatched: string[] = [];
  for (const piece of head.split("; ")) {
    const [name, dayList] = piece.split(": ");
    const meal = FOOD_BEVERAGE_MEAL_OPTIONS.find((m) => m === name);
    const days = dayList === undefined ? [] : dayList.split(", ").map((label) => dateByLabel.get(label));
    if (!meal || meals[meal] || days.some((d) => d === undefined)) {
      unmatched.push(piece);
      continue;
    }
    meals[meal] = (days as string[]).sort();
  }
  const rest = splitAt === -1 ? "" : value.slice(splitAt + 2);
  return { needed: true, meals, details: [unmatched.join("; "), rest].filter(Boolean).join(". ") };
}
